import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { addLocale, getLocale, removeLocale } from '@epi-helpers/UrlHelpers';
import {
  MANAGE_EMPLOYEES_SEARCH_NAME,
  TabsManageEmployees
} from '@epi-constants/manageEmployees';
import { useGetDetailsQuery } from '@epi-api/user';
import { useOnboardingWizard } from '@epi-shared/hooks/useOnboardingWizard';
import { IPagePanelFooter } from '@epi-models/containers/LoadCompanyAccount';

export const useManageEmployeesTabs = () => {
  const { t } = useTranslation();
  const [showFooter, setShowFooter] = useState(false);
  const [settingsFooter, setSettingsFooter] = useState<
    IPagePanelFooter | undefined
  >(undefined);
  const { search, pathname } = useLocation();
  const dispatch = useDispatch();
  const { data: customerData } = useGetDetailsQuery();
  const { isOnBoardingActive, flags, handleGoOnboardingPayment } =
    useOnboardingWizard();
  const hasEmployees = customerData?.isEmployeesSet;

  const locale = getLocale(pathname) || 'fi';

  const getTabPath = tabName => {
    let path;
    switch (tabName) {
      case TabsManageEmployees.massUpload:
        path = routerPaths.beneficiariesHrImportHistory;
        break;
      case TabsManageEmployees.settings:
        path = routerPaths.beneficiariesSettings;
        break;
      default:
        path = routerPaths.beneficiaries;
        break;
    }
    return addLocale(locale, path);
  };

  const handleFooterVisibility = tabName =>
    setShowFooter(tabName === TabsManageEmployees.employees);

  const handleURLChange = tabName => dispatch(goToPage(getTabPath(tabName)));

  const handleOnTabChange = tabName => {
    handleFooterVisibility(tabName);
    handleURLChange(tabName);
  };

  const urlParams = new URLSearchParams(search);
  const indexType = urlParams.get(MANAGE_EMPLOYEES_SEARCH_NAME);
  const pathWithoutLocale = removeLocale(pathname);
  let pathEnd = pathWithoutLocale.length;
  while (pathEnd > 0 && pathWithoutLocale[pathEnd - 1] === '/') {
    pathEnd -= 1;
  }
  const currentPath = pathWithoutLocale.slice(0, pathEnd) || '/';

  let defaultTabNameByPath;
  switch (currentPath) {
    case routerPaths.beneficiariesHrImportHistory:
      defaultTabNameByPath = TabsManageEmployees.massUpload;
      break;
    case routerPaths.beneficiariesSettings:
      defaultTabNameByPath = TabsManageEmployees.settings;
      break;
    default:
      defaultTabNameByPath = TabsManageEmployees.employees;
      break;
  }

  const defaultTabName =
    indexType && TabsManageEmployees[indexType]
      ? TabsManageEmployees[indexType]
      : defaultTabNameByPath;

  const getFooter = () => {
    if (isOnBoardingActive) {
      return currentPath === routerPaths.beneficiariesSettings
        ? {
            ...settingsFooter,
            cancelText: t('components.onboarding.buttons.next'),
            onCancel: handleGoOnboardingPayment,
            disableSubmit: !flags?.isEmployeesSet,
            submitButtonWidth: '240px',
            backText: t('components.onboarding.buttons.back'),
            onBack: () => {
              dispatch(goToPage(routerPaths.benefitGroups));
            }
          }
        : {
            backText: t('components.onboarding.buttons.back'),
            submitText: t('components.onboarding.buttons.next'),
            onBack: () => {
              dispatch(goToPage(routerPaths.benefitGroups));
            },
            onSubmit: handleGoOnboardingPayment,
            disableSubmit: !flags?.isEmployeesSet,
            submitButtonWidth: '240px'
          };
    }

    return currentPath === routerPaths.beneficiariesSettings
      ? settingsFooter
      : undefined;
  };

  return {
    footer: getFooter(),
    showFooter,
    handleOnTabChange,
    defaultTabName,
    hasEmployees,
    setSettingsFooter
  };
};
